import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import DataTableFilters from "./DataTableFilters";
import { Spinner } from "react-bootstrap";
import { openCenteredModal } from "../../REDUX/actions/centeredModal";
import { useDispatch } from "react-redux";
import UpdateCommentDialog from "../Avoir/UpdateComment";

const tableHeaders = (t, headers) => headers.map((header) => t(header));

const DataTable = ({
  newItemTitle = "Add a prospect",
  headers,
  columns,
  fetchData = () => {},
  data,
  totalPages,
  title,
  titleClassName,
  loading,
  filterElements,
  selectOptions,
  noResultsMessage,
  filters,
  addNewItem,
  setFilters,
  containerClassName = "pt-5",
  total,
  showRowDetails = true,
  view = "prospect",
  onRowClick,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  const handleSearch = _.debounce((e) => {
    setSearch(e.target.value);
  }, 300);

  const handleRowClick = (rowId) => {
    if (onRowClick) {
      onRowClick(rowId);
    } else if (showRowDetails) {
      setExpandedRow((prev) => (prev === rowId ? null : rowId));
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData(1, search, filters, controller.signal);
    return () => controller.abort();
  }, [filters, search]);

  const renderRowDetails = (row) => {
    if (view === "prospect") {
      const commentNote = row?.attributes?.note?.data?.attributes?.comment;
      const commentAction = row?.attributes?.actions?.data?.find(
        (action) => action?.attributes?.isReminder,
      )?.attributes?.comment;

      return (
        <tr className="table-blue-200 medium overflow-hidden">
          <td colSpan={headers.length} className="p-0">
            <table className="table-transparent w-full table m-0">
              <tbody>
                <tr className="table-blue-200 d-flex gap-3 medium">
                  <td className="py-3 px-5 d-flex col-6">
                    <div className="d-flex normal gap-1">
                      <p className="f14 text-lightBlack sBold mb-0 text-nowrap">
                        {t("Comment Note")} :
                      </p>
                      <p className="f14 text-lightBlack sBold mb-0">
                        {commentNote ?? t("No comment")}
                      </p>
                    </div>
                  </td>
                  <td className="py-3 px-5 d-flex col-6">
                    <div className="d-flex normal gap-1 ms-auto">
                      <p className="f14 text-lightBlack sBold mb-0 text-nowrap">
                        {t("Comment Reminder")} :
                      </p>
                      <p className="f14 text-lightBlack sBold mb-0">
                        {commentAction ?? t("No comment")}
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      );
    }
    // Espace clients › Client (flat rows): the project's comment + last action.
    if (view === "client") {
      const lastAction = row?.clientOrder?.lastAction;
      const details = [
        ["Comment Note", row?.clientOrder?.comment || t("No comment")],
        [
          "Last action",
          lastAction
            ? `${t(`${lastAction.type ?? ""}`)} — ${new Date(
                lastAction.date,
              ).toLocaleDateString("fr-FR")}`
            : "-",
        ],
      ];
      return (
        <tr className="table-blue-200 medium overflow-hidden">
          <td colSpan={headers.length} className="p-0">
            <table className="table-transparent w-full table m-0">
              <tbody>
                <tr className="table-blue-200 d-flex gap-3 medium">
                  {details.map(([label, value]) => (
                    <td className="py-3 px-5 d-flex col-6" key={label}>
                      <div className="d-flex normal gap-1">
                        <p className="f14 text-lightBlack sBold mb-0 text-nowrap">
                          {t(label)} :
                        </p>
                        <p className="f14 text-lightBlack sBold mb-0">
                          {value}
                        </p>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      );
    }
    if (view === "credit") {
      const comment = row?.attributes?.comment;

      return (
        <tr className="table-blue-200 medium overflow-hidden">
          <td colSpan={headers.length} className="p-0">
            <table className="table-transparent w-full table m-0">
              <tbody>
                <tr className="table-blue-200 d-flex gap-3 medium">
                  <td className="py-3 px-5 d-flex col-12">
                    <div
                      className="d-flex normal gap-1 cursor-pointer"
                      onClick={() =>
                        dispatch(
                          openCenteredModal(
                            <UpdateCommentDialog
                              id={row.id}
                              comment={row.attributes.comment}
                            />,
                          ),
                        )
                      }
                    >
                      <p className="f14 text-lightBlack sBold mb-0 text-nowrap">
                        {t("Comment")} :
                      </p>
                      <p className="f14 text-lightBlack sBold mb-0 text-start">
                        {comment ?? t("No comment")}
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      );
    }
    return null;
  };

  return (
    <div className={containerClassName}>
      <DataTableFilters
        filters={filters}
        title={title}
        titleClassName={titleClassName}
        elements={filterElements}
        selectOptions={selectOptions}
        newItemTitle={newItemTitle}
        addNewItem={addNewItem}
        handleSearch={handleSearch}
        setFilters={setFilters}
        total={total}
      />
      <div className="overflow-x-auto mt-2 pb-5 flex-grow-1 h-100">
        <table className="table table-transparent text-center">
          <thead className="table-dimGrey text-nowrap">
            <tr className="border-none">
              {tableHeaders(t, headers).map((header, index) => (
                <th
                  key={index}
                  scope="col"
                  className="py-3 px-4 text-lightBlack sBold f14"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0
              ? data.map((row, rowIndex) => {
                  const expanded = expandedRow === row.id;
                  return (
                    <Fragment key={row?.id}>
                      <tr
                        onClick={() => handleRowClick(row.id)}
                        className={`pointer align-middle table-row ${
                          expanded ? "table-blue expanded" : ""
                        }`}
                      >
                        {columns.map(
                          (column, index) =>
                            !column.hidden && (
                              <td
                                key={column.name}
                                className={`py-3 text-nowrap f14 text-lightBlack d-table-cell ${
                                  index >= columns.length - 1
                                    ? "ms-auto px-1"
                                    : "px-4"
                                } ${
                                  expanded ? "text-white" : "text-brightGrey"
                                }`}
                              >
                                {column.render({ ...row, index: rowIndex })}
                              </td>
                            ),
                        )}
                      </tr>
                      {expanded && renderRowDetails(row)}
                    </Fragment>
                  );
                })
              : !loading && (
                  <tr>
                    <td
                      colSpan={columns?.length}
                      className="py-3 px-4 text-center f14 text-lightBlack"
                    >
                      {t(noResultsMessage)}
                    </td>
                  </tr>
                )}
          </tbody>
        </table>
        {loading && (
          <div className="text-center w-100 pt-3 pb-1">
            <Spinner />
          </div>
        )}
        {totalPages > page && (
          <div className="w-100 text-center">
            <button
              className="text-brightGrey underline bg-transparent border-0 sBold outline-none"
              onClick={() => {
                const controller = new AbortController();
                setPage((prev) => prev + 1);
                fetchData(page + 1, search, filters, controller.signal);
              }}
            >
              {t("See more")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataTable;
